import React, { useEffect } from 'react';
import { Calendar, Coins, Building, TrendingUp, Info } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ComponentLifetime, TemplatesByType } from '../Förvaltning/TemplatesEditor/types';
import { Building as BuildingType } from '../../types/protocols';
import { BaseType } from '../../types/Enums';
import { BuildingType as BuildingTypeEnum } from '../../types/BuildingTypeEnums';
import { translate } from '../../types/EnumTranslations';
import { SERVICE_COLORS } from '../../Helpers/chartColors';
import CustomMultiSelect, { CustomMultiSelectOption } from '../../Components/CustomMultiSelect';
import { YearlyActivity, sortActivitiesByDate } from '../../Helpers/maintenanceCostUtils';
import MaintenanceActivityList from '../../Components/MaintenanceActivityList';

interface MaintenanceCostsTabProps {
    buildings: Record<string, BuildingType>;
    activePlans: Record<string, any>;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    componentConditions: Record<string, Record<string, any>>;
    loading: boolean;
    yearsToShow: number;
    setYearsToShow: (years: number) => void;
    yearlyPlans: YearlyActivity[];
    selectedBuildingTypes: BuildingTypeEnum[];
    setSelectedBuildingTypes: (types: BuildingTypeEnum[]) => void;
    adjustForInflation: boolean;
    setAdjustForInflation: (adjust: boolean) => void;
    buildingTypeOptions: CustomMultiSelectOption[];
}

const MaintenanceCostsTab: React.FC<MaintenanceCostsTabProps> = ({
    buildings,
    activePlans,
    componentLifetimes,
    maintenanceTemplates,
    componentConditions,
    loading,
    yearsToShow,
    setYearsToShow,
    yearlyPlans,
    selectedBuildingTypes,
    setSelectedBuildingTypes,
    adjustForInflation,
    setAdjustForInflation,
    buildingTypeOptions
}) => {
    const totalActivities = yearlyPlans.reduce((total, year) => total + year.activities.length, 0);
    const totalCost = yearlyPlans.reduce((total, year) => total + year.totalCost, 0);
    
    // Count only buildings that match the selected building types and have active plans
    const activeBuildingsCount = Object.keys(activePlans).filter(buildingKey => {
        const building = buildings[buildingKey];
        return building?.summary?.buildingType && selectedBuildingTypes.includes(building.summary.buildingType);
    }).length;

    // Initialize Bootstrap tooltips
    useEffect(() => {
        const initializeTooltips = () => {
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            return tooltipTriggerList.map((tooltipTriggerEl) => {
                if ((window as any).bootstrap && (window as any).bootstrap.Tooltip) {
                    return new (window as any).bootstrap.Tooltip(tooltipTriggerEl, {
                        container: 'body',
                        boundary: 'viewport'
                    });
                }
                return null;
            }).filter(Boolean);
        };

        // Try to initialize immediately
        let tooltipList = initializeTooltips();
        
        // If Bootstrap isn't available yet, wait a bit and try again
        if (tooltipList.length === 0) {
            const timeoutId = setTimeout(() => {
                tooltipList = initializeTooltips();
            }, 100);
            
            return () => {
                clearTimeout(timeoutId);
                tooltipList.forEach(tooltip => tooltip.dispose());
            };
        }

        // Cleanup tooltips when component unmounts
        return () => {
            tooltipList.forEach(tooltip => tooltip.dispose());
        };
    }, [adjustForInflation]); // Re-initialize when inflation setting changes

    const CostProjectionChart: React.FC<{ yearlyPlans: YearlyActivity[] }> = ({ yearlyPlans }) => {
        // Get all unique BaseTypes across all years
        const allBaseTypes = new Set<string>();
        yearlyPlans.forEach(plan => {
            Object.keys(plan.costsByBaseType).forEach(baseType => {
                allBaseTypes.add(baseType);
            });
        });
        const baseTypeKeys = Array.from(allBaseTypes).sort();

        const chartData = yearlyPlans.map(plan => {
            const data: any = {
                year: plan.year.toString(),
                totalCost: plan.totalCost,
                activities: plan.activities // Include activities for tooltip
            };
            
            // Add each BaseType as a separate data key
            baseTypeKeys.forEach(baseType => {
                data[baseType] = plan.costsByBaseType[baseType] || 0;
            });
            
            return data;
        });

        return (
            <div className="cost-chart-section">
                <div className="cost-chart-card">
                    <div className="chart-header">
                        <h3 className="chart-title">
                            <TrendingUp size={20} className="me-2" />
                            Aggregerad kostnadsprognos
                        </h3>
                        <p className="chart-subtitle">
                            Totala planerade kostnader för alla byggnader per år för de kommande {yearsToShow} åren
                        </p>
                    </div>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="year"
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    tickMargin={20}
                                    interval="preserveStartEnd"
                                />
                                <YAxis 
                                    label={{ value: 'Kostnad (kr)', angle: -90, position: 'insideLeft', offset: -15 }}
                                    tickFormatter={(value) => Math.round(value).toLocaleString('sv-SE')}
                                />
                                <Tooltip 
                                    formatter={(value: number, name: string, props: any) => {
                                        const payload = props.payload;
                                        if (adjustForInflation && payload?.activities) {
                                            // Find activities for this base type that have inflation data
                                            const relevantActivities = payload.activities.filter((activity: any) => 
                                                activity.baseType === name && activity.originalCost && activity.inflationYears > 0
                                            );
                                            
                                            if (relevantActivities.length > 0) {
                                                const totalOriginalCost = relevantActivities.reduce((sum: number, activity: any) => 
                                                    sum + activity.originalCost, 0
                                                );
                                                const avgInflationYears = relevantActivities.reduce((sum: number, activity: any) => 
                                                    sum + activity.inflationYears, 0
                                                ) / relevantActivities.length;
                                                const avgInflationPercent = relevantActivities.reduce((sum: number, activity: any) => 
                                                    sum + (activity.inflationMultiplier - 1) * 100, 0
                                                ) / relevantActivities.length;
                                                
                                                return [
                                                    `${Math.round(value).toLocaleString('sv-SE')} kr\n(Ursprunglig: ${Math.round(totalOriginalCost).toLocaleString('sv-SE')} kr, +${Math.round(avgInflationPercent)}% över ${Math.round(avgInflationYears)} år)`,
                                                    translate(name as BaseType) || name
                                                ];
                                            }
                                        }
                                        return [
                                            `${Math.round(value).toLocaleString('sv-SE')} kr`,
                                            translate(name as BaseType) || name
                                        ];
                                    }}
                                    labelFormatter={(label) => `År ${label}`}
                                />
                                <Legend 
                                    formatter={(value) => translate(value as BaseType) || value}
                                />
                                {baseTypeKeys.map((baseType, index) => (
                                    <Bar
                                        key={baseType}
                                        dataKey={baseType}
                                        stackId="costs"
                                        fill={SERVICE_COLORS[index % SERVICE_COLORS.length]}
                                        name={baseType}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Controls */}
            <div className="controls-section">
                <div className="row align-items-center mb-3">
                    <div className="col-md-4">
                        <div className="d-flex align-items-center">
                            <label htmlFor="yearsToShow" className="form-label me-3 mb-0">
                                Visa prognos för:
                            </label>
                            <select 
                                id="yearsToShow"
                                className="form-select"
                                value={yearsToShow}
                                onChange={(e) => setYearsToShow(parseInt(e.target.value, 10))}
                                style={{ width: 'auto' }}
                            >
                                <option value={5}>5 år</option>
                                <option value={10}>10 år</option>
                                <option value={25}>25 år</option>
                                <option value={50}>50 år</option>
                            </select>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <CustomMultiSelect
                            label="Byggnadstyp"
                            options={buildingTypeOptions}
                            value={selectedBuildingTypes}
                            onChange={(value) => setSelectedBuildingTypes(value)}
                            placeholder="Välj byggnadstyper"
                            maxSelectedLabels={2}
                            selectedItemsLabel="{0} byggnadstyper valda"
                        />
                    </div>
                    <div className="col-md-4">
                        <div className="inflation-checkbox-container">
                            <label className="form-label">Inflation</label>
                            <div className="form-check d-flex align-items-center">
                                <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    id="adjustForInflation"
                                    checked={adjustForInflation}
                                    onChange={(e) => setAdjustForInflation(e.target.checked)}
                                />
                                <label className="form-check-label me-2" htmlFor="adjustForInflation">
                                    Justera för inflation
                                </label>
                                <div 
                                    className="inflation-tooltip-container" 
                                    data-bs-toggle="tooltip" 
                                    data-bs-placement="top" 
                                    data-tooltip="Ökar kostnaderna med 2% per år från det datum när priset ursprungligen sattes"
                                    title="Ökar kostnaderna med 2% per år från det datum när priset ursprungligen sattes"
                                >
                                    <Info 
                                        size={16}
                                        className="inflation-tooltip-icon"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 text-center">
                        <div className="stats-summary justify-content-center">
                            <span className="badge bg-primary me-2">
                                <Building size={16} className="me-1" />
                                {activeBuildingsCount} byggnader
                            </span>
                            <span className="badge bg-success me-2">
                                <Calendar size={16} className="me-1" />
                                {totalActivities} aktiviteter
                            </span>
                            <span className="badge bg-warning">
                                <Coins size={16} className="me-1" />
                                {Math.round(totalCost).toLocaleString('sv-SE')} kr
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cost Projection Chart */}
            {yearlyPlans.length > 0 && (
                <CostProjectionChart yearlyPlans={yearlyPlans} />
            )}

            {/* Yearly Plans */}
            <div className="yearly-plans">
                {yearlyPlans.map((yearPlan) => (
                    <div key={yearPlan.year} className="year-card">
                        <div className="year-header">
                            <h3 className="year-title">{yearPlan.year}</h3>
                            <div className="year-stats">
                                <span className="activity-count me-3">
                                    {yearPlan.activities.length} aktivitet{yearPlan.activities.length !== 1 ? 'er' : ''}
                                </span>
                                {yearPlan.totalCost > 0 && (
                                    <span className="year-cost">
                                        <Coins size={16} className="me-1" />
                                        {Math.round(yearPlan.totalCost).toLocaleString('sv-SE')} kr
                                    </span>
                                )}
                            </div>
                        </div>
                        
                        {yearPlan.activities.length > 0 ? (
                            <MaintenanceActivityList 
                                activities={sortActivitiesByDate(yearPlan.activities)} 
                                showCostCalculation={true}
                                adjustForInflation={adjustForInflation}
                                showBuildingName={true}
                            />
                        ) : (
                            <div className="no-activities">
                                Inga planerade aktiviteter detta år
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {activeBuildingsCount === 0 && (
                <div className="no-plans-section">
                    <div className="alert alert-info">
                        <h4 className="alert-heading">Inga aktiva underhållsplaner</h4>
                        <p className="mb-0">
                            Det finns inga aktiva underhållsplaner att visa kostnader för. 
                            Skapa och aktivera underhållsplaner för att se ekonomiska prognoser.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaintenanceCostsTab;
